import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Navigation } from "../Landing/components/Navigation";
import { Footer } from "../Landing/components/Footer";
import { SmoothSection } from "../Landing/components/shared";
import type { Page } from "../types/Page";
import { useSecureVideoStream } from "../../hooks/useSecureVideoStream";
import watchDemoThumb from "../../assets/images/watch_demo_thumbnail.jpg";
import "./AboutPage.css";

const VANTAGE_VIDEO_URL =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260808_064556_051587f1-74a1-4336-8c05-4dde3594ed05.mp4";

export const AboutPage: Page = () => {
  const [isHiringModalOpen, setIsHiringModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const heroCardRef = useRef<HTMLDivElement>(null);

  // Secure Video Stream with Chunked Delivery, Blob URL Isolation & Viewport Auto-Pause
  const { videoProps } = useSecureVideoStream(videoRef, {
    src: VANTAGE_VIDEO_URL,
    containerRef: heroCardRef,
  });

  const stats = [
    { value: "2026", label: "Founded" },
    { value: "HIPAA", label: "Compliant Platform" },
    { value: "SOC 2", label: "Type II Ready" },
    { value: "24/7", label: "Expert Support" },
  ];

  const values = [
    {
      number: "01",
      title: "Domain Expert Quality",
      description:
        "We don't use crowd workers. Every validation is performed by domain specialists — board-certified radiologists, senior engineers, multilingual linguists — people who understand the difference between a good answer and a right answer.",
    },
    {
      number: "02",
      title: "Built for Complex Data",
      description:
        "From 3D DICOM volumetric MRI to structured NER extraction and LLM response evaluation, our platform handles the data types that generic tools can't. Multi-modal, multi-format, multi-language.",
    },
    {
      number: "03",
      title: "Privacy & Compliance First",
      description:
        "HIPAA, SOC 2, GDPR, and DPDP Act compliance. Whether you're handling patient data or proprietary model outputs, our infrastructure protects it at every layer.",
    },
  ];

  const scrollToStory = () => {
    const el = document.getElementById("our-story-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="about-page-root">
      {/* 1. SOLID BLACK NAVBAR */}
      <Navigation />

      {/* Embedded Component Styles for Zero-Flash Rendering */}
      <style>{`
        .about-hero-wrapper {
          padding-top: clamp(140px, 16vh, 175px);
          padding-bottom: clamp(32px, 4.5vh, 56px);
          padding-left: clamp(16px, 3.5vw, 40px);
          padding-right: clamp(16px, 3.5vw, 40px);
          max-width: 1360px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
        }
        .about-hero-card {
          position: relative;
          width: 100%;
          min-height: 340px;
          border-radius: 24px;
          overflow: hidden;
          background-color: #060707;
          border: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: inset 0 1px 0 0 rgba(255, 255, 255, 0.1), 0 20px 50px -10px rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
          box-sizing: border-box;
        }
        .about-hero-animation-box {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 54%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
          z-index: 1;
        }
        .about-hero-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          opacity: 0.85;
          display: block;
        }
        .about-hero-scrim {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 2;
          background: linear-gradient(90deg, #060707 0%, #060707 18%, rgba(6, 7, 7, 0.92) 38%, rgba(6, 7, 7, 0.45) 68%, rgba(6, 7, 7, 0.08) 88%, transparent 100%);
        }
        .about-hero-content {
          position: relative;
          z-index: 5;
          padding: clamp(28px, 3.5vw, 44px);
          width: 56%;
          max-width: 580px;
          display: flex;
          flex-direction: column;
          gap: 14px;
          box-sizing: border-box;
        }
        .about-vantage-title {
          font-family: 'Instrument Serif', 'Cormorant Garamond', Georgia, serif;
          font-weight: 500;
          line-height: 1.06;
          letter-spacing: -1px;
          margin: 0;
          font-size: clamp(34px, 3.6vw, 46px);
        }
        .about-vantage-line-one {
          display: block;
          color: #ffffff;
        }
        .about-vantage-line-two {
          display: block;
          color: rgba(211, 207, 207, 0.82);
        }
        .about-vantage-copy {
          font-family: 'Inter', -apple-system, sans-serif;
          color: rgba(226, 229, 228, 0.82);
          font-size: clamp(13px, 1.15vw, 14.5px);
          line-height: 1.55;
          margin: 0;
          max-width: 460px;
        }
        .about-hero-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 4px;
        }
        .about-primary-btn {
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          height: 38px;
          padding-left: 16px;
          padding-right: 4px;
          border-radius: 7px;
          background: #ffffff;
          color: #0e0e0e;
          font-family: 'Inter', sans-serif;
          font-size: 13.5px;
          font-weight: 600;
          letter-spacing: -0.2px;
          border: none;
          cursor: pointer;
          gap: 12px;
          text-decoration: none;
        }
        .about-primary-btn-arrow {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: #080808;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .about-hero-demo-anchor {
          position: absolute;
          right: clamp(24px, 3.5vw, 56px);
          top: 50%;
          transform: translateY(-50%);
          z-index: 5;
          pointer-events: auto;
        }
        .about-demo-card {
          width: 175px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          border-radius: 14px;
          background: linear-gradient(145deg, rgba(24, 22, 20, 0.85), rgba(5, 12, 14, 0.9));
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          padding: 8px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          box-sizing: border-box;
        }
        .about-demo-visual {
          position: relative;
          width: 100%;
          height: 115px;
          border-radius: 9px;
          overflow: hidden;
          background: #0f1315;
          cursor: pointer;
        }
        .about-demo-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .about-demo-play-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.35);
          background: rgba(3, 5, 7, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .about-demo-watch-btn {
          width: 100%;
          height: 32px;
          border-radius: 7px;
          border: 1px solid rgba(255, 255, 255, 0.18);
          background: linear-gradient(145deg, rgba(26, 34, 36, 0.86), rgba(16, 29, 33, 0.9));
          color: #ffffff;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        @media (max-width: 900px) {
          .about-hero-demo-anchor {
            display: none !important;
          }
          .about-hero-card {
            min-height: 290px;
            max-height: 380px;
          }
          .about-hero-content {
            width: 100%;
            max-width: 100%;
            padding: 24px 20px;
          }
          .about-hero-animation-box {
            width: 100%;
            opacity: 0.35;
          }
          .about-hero-scrim {
            background: linear-gradient(0deg, #060707 0%, #060707 35%, rgba(6, 7, 7, 0.85) 60%, rgba(6, 7, 7, 0.4) 100%);
          }
          .about-vantage-title {
            font-size: 28px;
          }
          .about-vantage-copy {
            font-size: 13px;
          }
        }
      `}</style>

      {/* 2. CURVED HERO CARD: HEADING AND ANIMATION HERE (SMALL SECTION) */}
      <section className="about-hero-wrapper">
        <div
          ref={heroCardRef}
          className="about-hero-card"
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Confined Animation Box on Right */}
          <div className="about-hero-animation-box">
            <video
              ref={videoRef}
              className="about-hero-video"
              aria-hidden="true"
              {...videoProps}
            />
            {/* Scrim Gradient for text contrast */}
            <div className="about-hero-scrim" />
            <div className="about-hero-bottom-scrim" />
          </div>

          {/* Left Hero Content: Headline, Body Copy, CTA */}
          <div className="about-hero-content">
            <div className="about-hero-badge">
              <span>// About Cynaps</span>
            </div>

            <h1 className="about-vantage-title">
              <span className="about-vantage-line-one">Independent Validation</span>
              <span className="about-vantage-line-two">For Trustworthy AI.</span>
            </h1>

            <p className="about-vantage-copy">
              Your AI outputs need more than spot-checking.
              <br />
              Cynaps provides measurable, multi-reviewer
              <br />
              consensus with audit-ready quality reports.
            </p>

            <div className="about-hero-actions">
              <button
                type="button"
                onClick={scrollToStory}
                className="about-primary-btn"
              >
                <span>Get Started</span>
                <div className="about-primary-btn-arrow">
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2.5 7H11.5M11.5 7L7.5 3M11.5 7L7.5 11"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. MAIN CONTENT HERE (KEPT INTACT FROM ORIGINAL) */}

      {/* Stats Section */}
      <SmoothSection className="py-20 bg-black border-t border-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-500 font-mono text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </SmoothSection>

      {/* Our Story Section */}
      <SmoothSection className="py-32 bg-black border-t border-gray-900" id="our-story-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-start gap-4 mb-8">
              <div className="flex flex-col items-start">
                <div className="w-6 h-[2px] bg-gray-600" />
                <div className="w-[2px] h-6 bg-gray-600" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white">Our Story</h2>
            </div>

            <div className="space-y-6 text-gray-400 font-mono text-lg leading-relaxed">
              <p>
                Cynaps was born out of a real problem: AI teams had no systematic way to validate their data and model outputs. The standard approach — one person visually spot-checking samples — produced no measurement, no audit trail, and no confidence. We built the infrastructure to change that.
              </p>
              <p>
                Started by a Computer Science researcher focused on AI infrastructure, Cynaps was designed from first principles to solve this validation gap. We built multi-reviewer consensus workflows, automated agreement scoring, DICOM-native medical image support, and strict PHI protections.
              </p>
              <p>
                Today, Cynaps provides independent validation infrastructure for healthcare AI companies, biotech startups, and engineering teams — delivering the measurable quality assurance needed to deploy AI with confidence.
              </p>
            </div>
          </motion.div>
        </div>
      </SmoothSection>

      {/* Values Section */}
      <SmoothSection className="py-32 bg-black border-t border-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-white mb-16"
          >
            What we believe
          </motion.h2>

          <div className="space-y-12">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border-l-2 border-gray-800 pl-8 hover:border-emerald-500 transition-colors"
              >
                <div className="text-gray-500 font-mono text-sm mb-2">{value.number}/</div>
                <h3 className="text-2xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 font-mono leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </SmoothSection>

      {/* CTA Section */}
      <SmoothSection className="py-32 bg-black border-t border-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Join us in building trustworthy AI
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-gray-400 font-mono mb-12 text-lg"
          >
            We're always looking for talented people who share our vision.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsHiringModalOpen(true)}
              className="bg-white text-black px-10 py-4 text-base font-semibold transition-all hover:bg-gray-100"
            >
              View Open Positions →
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.05)" }}
              whileTap={{ scale: 0.98 }}
              onClick={() => (window.location.href = "/contact")}
              className="border border-gray-700 text-white px-10 py-4 text-base font-semibold transition-all hover:border-gray-500"
            >
              Get in Touch
            </motion.button>
          </motion.div>
        </div>
      </SmoothSection>

      {/* 4. Original Cynaps Footer */}
      <Footer />

      {/* 5. Video Lightbox Modal */}
      <AnimatePresence>
        {isVideoModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="vantage-modal-overlay"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="vantage-modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="vantage-modal-close"
                onClick={() => setIsVideoModalOpen(false)}
                aria-label="Close video"
              >
                ✕
              </button>
              <video
                controls
                autoPlay
                className="vantage-modal-video"
                src={VANTAGE_VIDEO_URL}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 6. Hiring Modal */}
      <AnimatePresence>
        {isHiringModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setIsHiringModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-black border border-gray-800 p-8 shadow-2xl"
            >
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-4 h-[1px] bg-emerald-500" />
              <div className="absolute top-0 left-0 w-[1px] h-4 bg-emerald-500" />
              <div className="absolute bottom-0 right-0 w-4 h-[1px] bg-emerald-500" />
              <div className="absolute bottom-0 right-0 w-[1px] h-4 bg-emerald-500" />

              <div className="text-center">
                <div className="text-gray-500 font-mono text-xs uppercase tracking-wider mb-4">
                  // Status: Full Capacity
                </div>

                <h3 className="text-2xl font-bold text-white mb-4">No Open Positions</h3>

                <p className="text-gray-400 font-mono text-sm leading-relaxed mb-8">
                  We are currently full and not seeking new team members at the moment. However, we are always
                  growing—please check back soon!
                </p>

                <motion.button
                  whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.05)" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsHiringModalOpen(false)}
                  className="w-full border border-gray-700 text-white px-6 py-3 text-sm font-semibold transition-all hover:border-gray-500"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

AboutPage.title = "About";
AboutPage.path = "/about";
AboutPage.exact = true;

export default AboutPage;
